import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { combineLatest, of, switchMap } from 'rxjs';
import { IconCacheService } from '../../../core/services/cache/icon-cache.service';

@Component({
  selector: 'app-icon',
  standalone: true,
  templateUrl: './icon.component.html',
  styleUrl: './icon.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IconComponent {
  private readonly iconCache = inject(IconCacheService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly destroyRef = inject(DestroyRef);

  name = input('');
  colour = input<string | undefined>();
  size = input<string | undefined>('24');

  svgIcon = signal<SafeHtml>('');

  constructor() {
    const name$ = toObservable(this.name);
    const colour$ = toObservable(this.colour);
    const size$ = toObservable(this.size);

    combineLatest([name$.pipe(switchMap((name) => (name ? this.iconCache.getSvg(name) : of('')))), colour$, size$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ([rawSvg, colour, size]) => {
          if (!rawSvg) {
            this.svgIcon.set(this.sanitizer.bypassSecurityTrustHtml(''));
            return;
          }
          const styled = this.applySvgAttributes(rawSvg, colour, size ?? '24');
          this.svgIcon.set(this.sanitizer.bypassSecurityTrustHtml(styled));
        },
        error: (err) => console.error('Icon loading failed', err),
      });
  }

  private applySvgAttributes(svgContent: string, color?: string, size = '24'): string {
    if (!svgContent) return '';

    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svgContent, 'image/svg+xml');
    const svgElement = svgDoc.documentElement;

    svgElement.setAttribute('width', size);
    svgElement.setAttribute('height', size);

    if (color) {
      const shapes = svgDoc.querySelectorAll('path');
      shapes.forEach((shape) => {
        shape.setAttribute('fill', color);
        if (shape.hasAttribute('stroke')) {
          shape.setAttribute('stroke', color);
        }
      });
    }

    const serializer = new XMLSerializer();
    return serializer.serializeToString(svgDoc.documentElement);
  }
}
